import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CHAIR, GRID, chairPositions, roomSize } from "./planLogic";

// Pianta di una sala: tavoli posizionati su un piano logico (room.width x room.height), scalato alla larghezza disponibile.
// Con `editable` i tavoli si trascinano (onMove riceve la posizione grezza: aggancio e limiti li applica chi chiama);
// senza, si possono solo cliccare. `onClear`: clic sull'area vuota.
const GOLD = "#b8932f";
const COLORS = {
  free: { bg: "#fff", border: "#8a8170", text: "#1d1b17" },
  booked: { bg: "#f3dd9a", border: "#8a6d1c", text: "#1d1b17" },
  pending: { bg: "#ffe0b2", border: "#e08a00", text: "#1d1b17" },
  arrived: { bg: "#bfe5c6", border: "#2e7d32", text: "#1d1b17" },
};

const PlanCanvas = ({ room, tables, selected, editable, onSelect, onMove, onClear }) => {
  const { t } = useTranslation("bookings");
  const box = useRef(null);
  const [scale, setScale] = useState(1);
  const drag = useRef(null);
  const { w: W, h: H } = roomSize(room);

  useEffect(() => {
    const fit = () => box.current && setScale(Math.min(1, box.current.clientWidth / W));
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [W]);

  const down = (e, tb) => {
    e.stopPropagation();
    onSelect && onSelect(tb);
    if (!editable) return;
    drag.current = { id: tb.id || tb.key, sx: e.clientX, sy: e.clientY, x: tb.x, y: tb.y, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const move = (e, tb) => {
    const d = drag.current;
    if (!d || d.id !== (tb.id || tb.key)) return;
    const dx = (e.clientX - d.sx) / scale;
    const dy = (e.clientY - d.sy) / scale;
    if (!d.moved && Math.abs(dx) < 3 && Math.abs(dy) < 3) return; // un semplice clic non sposta
    d.moved = true;
    onMove && onMove(tb, d.x + dx, d.y + dy);
  };

  return (
    <div ref={box} style={{ width: "100%", height: H * scale }}>
      <div
        className="plan-canvas"
        onPointerDown={(e) => e.target === e.currentTarget && onClear && onClear()}
        style={{
          width: W,
          height: H,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          position: "relative",
          background: `repeating-linear-gradient(0deg,#e6dfcc 0 1px,transparent 1px ${GRID}px),repeating-linear-gradient(90deg,#e6dfcc 0 1px,transparent 1px ${GRID}px),#faf7ef`,
          border: "2px solid #cfc8b8",
          borderRadius: 4,
          boxSizing: "content-box",
        }}
      >
        {tables.map((tb) => {
          const c = COLORS[tb.state || "free"] || COLORS.free;
          const key = tb.id || tb.key;
          const isSel = selected === key;
          const off = tb.active === false;
          const rot = tb.rotation || 0;
          const border = isSel ? (editable ? GOLD : "#1d1b17") : c.border;
          return (
            <div
              key={key}
              onPointerDown={(e) => down(e, tb)}
              onPointerMove={(e) => move(e, tb)}
              onPointerUp={() => (drag.current = null)}
              onPointerCancel={() => (drag.current = null)}
              title={tb.booking ? `${tb.booking.name} · ${t("floor.persons", { n: tb.booking.party_size })}` : tb.name}
              style={{
                position: "absolute",
                left: tb.x,
                top: tb.y,
                width: tb.width,
                height: tb.height,
                transform: `rotate(${rot}deg)`,
                cursor: editable ? "grab" : "pointer",
                touchAction: "none",
                userSelect: "none",
                opacity: off ? 0.45 : 1,
                zIndex: isSel ? 2 : 1,
              }}
            >
              {chairPositions(tb).map((ch, i) => (
                <span
                  key={i}
                  style={{
                    position: "absolute",
                    left: tb.width / 2 + ch.x - CHAIR / 2,
                    top: tb.height / 2 + ch.y - CHAIR / 2,
                    width: CHAIR,
                    height: CHAIR,
                    transform: `rotate(${ch.a}deg)`,
                    background: "#e7dcc0",
                    border: `1px ${off ? "dashed" : "solid"} #a39674`,
                    borderRadius: "6px 6px 3px 3px",
                    pointerEvents: "none",
                  }}
                />
              ))}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: c.bg,
                  border: `${isSel ? 3 : 2}px ${off ? "dashed" : "solid"} ${border}`,
                  borderRadius: tb.shape === "round" ? "50%" : 6,
                  boxShadow: isSel && editable ? `0 0 0 3px rgba(184,147,47,.3)` : "none",
                }}
              />
              {/* la scritta resta dritta anche col tavolo ruotato */}
              <div
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transform: `translate(-50%,-50%) rotate(${-rot}deg)`,
                  color: c.text,
                  textAlign: "center",
                  lineHeight: 1.15,
                  whiteSpace: "nowrap",
                  pointerEvents: "none",
                }}
              >
                <strong style={{ fontSize: 14 }}>{tb.name}</strong>
                <div style={{ fontSize: 10 }}>
                  {tb.booking ? `${tb.booking.party_size}p · ${tb.booking.time}` : t("plan.seats", { min: tb.seats_min, max: tb.seats_max })}
                </div>
                {off && <div style={{ fontSize: 10, fontStyle: "italic" }}>{t("plan.notInUse")}</div>}
                {!off && tb.joinable === false && <div style={{ fontSize: 10, fontStyle: "italic" }}>{t("plan.notJoinable")}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PlanCanvas;
